import type { ComponentProps, ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import { tv } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";

type TimelineProps = ComponentProps<"ul">;

export function Timeline({ children, className, ...props }: TimelineProps) {
  const { list, scroll } = timelineStyles();

  return (
    <div className={scroll()}>
      <ul className={cn(list(), className)} {...props}>
        {children}
      </ul>
    </div>
  );
}

type TimelineItemProps = {
  children: ReactNode;
  className?: string;
  isLatest?: boolean;
};

export function TimelineItem({ children, className, isLatest = false }: TimelineItemProps) {
  const { dot, item } = timelineStyles({ isLatest });

  return (
    <li className={cn(item(), className)}>
      <div className={dot()} />
      {children}
    </li>
  );
}

type TimelineGroupProps = {
  children: ReactNode;
  className?: string;
};

export function TimelineGroup({ children, className }: TimelineGroupProps) {
  const { group, groupDot, groupLabel } = timelineStyles();

  return (
    <li className={cn(group(), className)}>
      <div className={groupDot()} />
      <span className={groupLabel()}>{children}</span>
    </li>
  );
}

type TimelineTimestampProps = {
  className?: string;
  dateTime: string;
};

export function TimelineTimestamp({ className, dateTime }: TimelineTimestampProps) {
  const timestamp = dayjs(dateTime);
  const { timestamp: timestampStyle, timestampTime } = timelineStyles();

  return (
    <time className={cn(timestampStyle(), className)} dateTime={dateTime}>
      {timestamp.format("MM/DD/YYYY")}{" "}
      <span className={timestampTime()}>{timestamp.format("h:mm A")}</span>
    </time>
  );
}

type TimelineDetailsProps = ComponentProps<"dl">;

export function TimelineDetails({ children, className, ...props }: TimelineDetailsProps) {
  const { details } = timelineStyles();

  return (
    <dl className={cn(details(), className)} {...props}>
      {children}
    </dl>
  );
}

type TimelineDetailProps = {
  label: string;
  value: ReactNode;
};

export function TimelineDetail({ label, value }: TimelineDetailProps) {
  const { detailLabel, detailValue } = timelineStyles();

  return (
    <>
      <dt className={detailLabel()}>{label}</dt>
      <dd className={detailValue()}>{value}</dd>
    </>
  );
}

type TimelineSkeletonProps = {
  itemCount?: number;
};

export function TimelineSkeleton({ itemCount = 3 }: TimelineSkeletonProps) {
  return (
    <Timeline>
      {Array.from({ length: itemCount }).map((_, i) => (
        <TimelineItem key={i}>
          <div className="tw:flex tw:min-w-40 tw:flex-col tw:gap-y-1 tw:border-r tw:border-fill-quaternary tw:pr-4">
            <Skeleton height={16} width={120} />
            <Skeleton borderRadius={4} height={20} width={60} />
          </div>

          <TimelineDetails>
            <Skeleton height={16} width={50} />
            <Skeleton height={20} width={80} />

            <Skeleton height={16} width={90} />
            <Skeleton height={16} width={130} />

            <Skeleton height={16} width={70} />
            <Skeleton height={16} width="80%" />

            <Skeleton height={16} width={110} />
            <Skeleton height={16} width="60%" />
          </TimelineDetails>
        </TimelineItem>
      ))}
    </Timeline>
  );
}

const timelineStyles = tv({
  slots: {
    detailLabel: "tw:font-bold",
    details: "tw:grid tw:w-full tw:grid-cols-[160px_1fr] tw:gap-y-2",
    detailValue: "tw:break-all",
    dot: "tw:absolute tw:-inset-s-6 tw:h-3 tw:w-3 tw:rounded-full tw:bg-fill-quaternary",
    group: [
      "tw:relative tw:ms-4 tw:flex tw:items-center tw:py-1",
      "tw:first:before:absolute tw:first:before:top-0 tw:first:before:bottom-1/2 tw:first:before:-left-4.25 tw:first:before:w-px tw:first:before:bg-fill-white",
    ],
    groupDot:
      "tw:absolute tw:-inset-s-6.75 tw:size-5 tw:rounded-full tw:border tw:border-stroke-bright-purple-secondary tw:bg-fill-white",
    groupLabel:
      "tw:rounded-full tw:bg-fill-bright-purple-quinary tw:px-3 tw:py-1 tw:text-xs tw:font-bold tw:tracking-wide tw:text-text-bright-purple-primary tw:uppercase",
    item: [
      "tw:relative tw:ms-4 tw:flex tw:gap-4 tw:rounded-lg tw:border tw:border-fill-quaternary tw:p-4",
      "tw:first:before:absolute tw:first:before:top-0 tw:first:before:-left-4.5 tw:first:before:h-5.5 tw:first:before:w-px tw:first:before:bg-fill-white",
      "tw:last:after:absolute tw:last:after:top-7 tw:last:after:bottom-0 tw:last:after:-left-4.5 tw:last:after:w-px tw:last:after:bg-fill-white",
    ],
    list: [
      "tw:relative tw:ml-3 tw:flex tw:flex-col tw:gap-y-2.5 tw:border-s tw:border-l tw:border-fill-quaternary tw:text-sm tw:text-text-primary",
      "tw:[@container_scroll-state(scrollable:y)]:mr-3",
    ],
    scroll: "tw:max-h-[70vh] tw:@container-[scroll-state] tw:overflow-y-auto",
    timestamp: "tw:text-xs",
    timestampTime: "tw:text-text-tertiary",
  },
  variants: {
    isLatest: {
      true: {
        item: "tw:bg-fill-senary",
      },
    },
  },
});
